import { useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle, ArrowRight, Award, Check, Globe2, Loader2, Sparkles } from "lucide-react";

import { OutcomeState } from "@/components/account/outcome-state";
import { Button } from "@/components/ui/button";
import { buttonVariants } from "@/components/ui/button-variants";
import { LoadErrorState } from "@/components/ui/load-error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useBrowsingCountry } from "@/lib/hooks/use-browsing-country";
import { showToast } from "@/lib/ui/toast";

interface FoundingSearch {
  country?: string;
}

interface FoundingResponse {
  enabled: boolean;
  slots: number;
  taken: number;
  slotsRemaining: number;
  lifetimePlanLabel: string;
  lifetimePlanPrice: number;
  lifetimeMonths: number;
  badge: boolean;
}

interface FoundingCheckoutResponse {
  success: boolean;
  subscriptionId?: string;
  /** @deprecated alias of `subscriptionId`, dropped after the next release. */
  lifetimeSubscriptionId?: string;
  /** ISO date the free period ends; null = for life. */
  endsAt?: string | null;
  message?: string;
}

export const Route = createFileRoute("/_authenticated/billing/founding")({
  validateSearch: (search: Record<string, unknown>): FoundingSearch => ({
    country:
      typeof search.country === "string" && /^[A-Za-z]{2}$/.test(search.country)
        ? search.country.toUpperCase()
        : undefined,
  }),
  component: FoundingEnrollPage,
});

function FoundingEnrollPage() {
  const { country: countryParam } = Route.useSearch();
  const { country: cookieCountry } = useBrowsingCountry();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useLingui();

  const country = countryParam ?? (cookieCountry ? cookieCountry.toUpperCase() : "RO");

  const query = useQuery({
    queryKey: ["api", "/api/pricing/founding", country],
    queryFn: () =>
      apiFetch.get<FoundingResponse>(
        `/api/pricing/founding?country=${encodeURIComponent(country)}`,
      ),
  });

  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsProviderProfile, setNeedsProviderProfile] = useState(false);

  const enroll = async () => {
    setEnrolling(true);
    setError(null);
    setNeedsProviderProfile(false);
    try {
      await apiFetch.post<FoundingCheckoutResponse>("/api/checkout/founding", {
        countryCode: country,
      });
      showToast(t`Welcome to the Founding Provider Program!`, "success");
      await queryClient.invalidateQueries({ queryKey: ["api", "/api/account/billing"] });
      navigate({ to: "/account/provider/billing", search: { tab: "subscription" } });
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined;
        if (err.status === 400 && body?.error?.includes("provider profile")) {
          setNeedsProviderProfile(true);
        } else {
          setError(body?.error ?? t`Could not complete enrollment. Please try again.`);
        }
      } else {
        setError(t`Could not complete enrollment. Please try again.`);
      }
    } finally {
      setEnrolling(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl pb-10">
      <header className="mb-6">
        <h1 className="font-display text-foreground text-2xl font-semibold tracking-[-0.02em]">
          <Trans>Founding Provider Program</Trans>
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          <Trans>Free premium access for the first doctors who join PontiMed.</Trans>
        </p>
      </header>

      {query.isLoading && <Skeleton className="h-72 w-full rounded-xl" />}

      {query.isError && (
        <LoadErrorState
          title={<Trans>Could not load the founding program.</Trans>}
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      )}

      {/* P16: unavailable → a next step instead of a dead end. */}
      {query.isSuccess && !query.data.enabled && (
        <OutcomeState
          className="border-border bg-card rounded-xl border"
          icon={Globe2}
          title={<Trans>The founding program is not available in your country yet.</Trans>}
          description={<Trans>You can still pick a doctor plan today.</Trans>}
          actions={
            <Link
              to="/billing/subscribe"
              search={{ target: "provider", interval: "monthly" }}
              className={buttonVariants({ size: "touch" })}
            >
              <Trans>See doctor plans</Trans>
              <ArrowRight className="size-4" aria-hidden />
            </Link>
          }
        />
      )}

      {query.isSuccess && query.data.enabled && (
        <div className="from-primary/15 to-accent/15 border-primary/20 rounded-xl border bg-gradient-to-br p-6">
          <div className="flex items-start gap-3">
            <div className="bg-primary text-primary-foreground flex size-12 shrink-0 items-center justify-center rounded-xl">
              <Sparkles className="size-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-foreground text-lg font-bold">
                <Trans>{query.data.slotsRemaining} founding slots left</Trans>
              </p>
              <p className="text-muted-foreground text-sm">
                <Trans>
                  {query.data.taken} of {query.data.slots} slots already taken
                </Trans>
              </p>
            </div>
          </div>

          <ul className="mt-5 space-y-2.5">
            <li className="text-foreground flex items-start gap-2 text-sm">
              <Check className="text-success mt-0.5 size-4 shrink-0" />
              {query.data.lifetimeMonths === 0 ? (
                <Trans>{query.data.lifetimePlanLabel} plan — free for life</Trans>
              ) : (
                <Trans>
                  {query.data.lifetimePlanLabel} plan — free for {query.data.lifetimeMonths} months
                </Trans>
              )}
            </li>
            {query.data.badge && (
              <li className="text-foreground flex items-start gap-2 text-sm">
                <Award className="text-accent mt-0.5 size-4 shrink-0" />
                <Trans>Permanent Founding Doctor badge on your profile</Trans>
              </li>
            )}
          </ul>

          {error && (
            <div className="border-destructive/30 bg-destructive-soft text-destructive-strong mt-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
              <AlertCircle className="size-4 shrink-0" />
              {error}
            </div>
          )}

          {needsProviderProfile && (
            <div className="border-warning/30 bg-warning-soft mt-4 rounded-lg border px-4 py-3">
              <p className="text-warning-strong text-sm font-medium">
                <Trans>You need a provider profile to join the founding program.</Trans>
              </p>
              <Button
                size="touch"
                variant="outline"
                className="mt-2"
                onClick={() =>
                  navigate({
                    to: "/billing/founding",
                    search: (prev) => ({ ...prev, add: "provider" }),
                  })
                }
              >
                <Trans>Add provider profile</Trans>
              </Button>
            </div>
          )}

          <Button
            className="mt-6 min-h-[44px] w-full gap-1.5 text-sm font-semibold"
            onClick={enroll}
            disabled={enrolling || query.data.slotsRemaining <= 0}
          >
            {enrolling && <Loader2 className="size-4 animate-spin" />}
            {query.data.slotsRemaining <= 0 ? (
              <Trans>All slots are taken</Trans>
            ) : (
              <Trans>Join the founding program — free</Trans>
            )}
          </Button>
          {query.data.slotsRemaining <= 0 && (
            <Link
              to="/billing/subscribe"
              search={{ target: "provider", interval: "monthly" }}
              className={cn(
                buttonVariants({ variant: "outline", size: "touch" }),
                "mt-2 w-full gap-1.5",
              )}
            >
              <Trans>See doctor plans</Trans>
              <ArrowRight className="size-4" aria-hidden />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
